
/* 
 * exif-utils.js
 * 
 * Helper functions to extract and insert EXIF (APP1) segments 
 * from JPEG binary data.
 */

// JPEG Markers
const SOI = 0xFFD8;
const APP1 = 0xFFE1;
// const SOS = 0xFFDA; // Not needed for extraction logic

/**
 * Extracts the APP1 (EXIF) segment from an ArrayBuffer of a JPEG image.
 * Returns null if no APP1 segment is found.
 * 
 * @param {ArrayBuffer} arrayBuffer - The raw binary data of the original image
 * @returns {ArrayBuffer|null} - The extracted APP1 segment (including marker and length)
 */
export function getExifSegment(arrayBuffer) {
    if (!(arrayBuffer instanceof ArrayBuffer)) {
        console.warn("Invalid input: expected ArrayBuffer");
        return null;
    }

    const view = new DataView(arrayBuffer);

    // Check for SOI
    if (view.getUint16(0) !== SOI) {
        console.warn("Not a valid JPEG");
        return null;
    }

    let offset = 2; // Start after SOI
    const length = view.byteLength;

    while (offset < length) {
        // Check next marker
        if (view.getUint8(offset) !== 0xFF) {
            // console.warn("Invalid marker found at offset " + offset);
            return null; // Lost valid stream
        }

        const marker = view.getUint8(offset + 1);

        // Check if it's APP1 (EXIF)
        // APP1 marker is 0xE1
        if (marker === 0xE1) {
            // Found EXIF!
            // Read length (2 bytes)
            const segmentLength = view.getUint16(offset + 2);
            // Return the whole segment: Marker (2) + Length (2) + Data (segmentLength - 2)
            // Actually segmentLength includes the 2 bytes for length itself.
            // So size = 2 (FF E1) + segmentLength

            const segmentStart = offset;
            const segmentEnd = offset + 2 + segmentLength;

            return arrayBuffer.slice(segmentStart, segmentEnd);
        }

        // Move to next segment
        offset += 2; // Skip marker bytes
        if (offset >= length) break;

        // Read segment length to skip it
        const segmentLength = view.getUint16(offset);
        offset += segmentLength;
    }

    return null; // Not found
}

/**
 * Inserts an extracted EXIF segment into a JPEG Blob.
 * 
 * @param {Blob} jpegBlob - The compressed JPEG blob (which lacks EXIF)
 * @param {ArrayBuffer} exifSegment - The binary APP1 segment to insert
 * @returns {Promise<Blob>} - A new Blob with EXIF inserted
 */
export async function insertExifSegment(jpegBlob, exifSegment) {
    if (!(jpegBlob instanceof Blob)) {
        throw new Error("Invalid input: jpegBlob must be a Blob");
    }
    if (exifSegment && !(exifSegment instanceof ArrayBuffer)) {
        throw new Error("Invalid input: exifSegment must be an ArrayBuffer");
    }

    const buffer = await jpegBlob.arrayBuffer();

    // Create a new buffer: SOI + EXIF + Rest of Image (minus SOI)
    // Original Blob: [FF D8] [Other Segments...]
    // New Blob: [FF D8] [APP1 Segment] [Other Segments...]

    const view = new DataView(buffer);

    if (view.getUint16(0) !== SOI) {
        console.warn("Target blob is not a valid JPEG");
        return jpegBlob; // Return original if fail
    }

    // Construct new blob parts
    const soi = buffer.slice(0, 2); // FF D8
    const rest = buffer.slice(2);   // Everything else

    return new Blob([soi, exifSegment, rest], { type: "image/jpeg" });
}
